<script setup lang="ts">
import { api } from '@/api';
import {
  type CreateDefinitionDtoValueType,
  type DefinitionDto,
  DefinitionParameterValueDto,
  DefinitionParameterValueDtoTypeEnum,
  type DefinitionParameterValueSetDto,
  DefinitionParameterValueSetDtoTypeEnum,
  ProtocolDto,
} from '@/api/generator';
import { getDefaultOptions } from '@/components/confirmationModals/defaults.ts';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation.ts';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import Select from '@/components/forms/Select/Select.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { FormMode } from '@/components/forms/types.ts';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import { formBackendErrorHandler } from '@/errorHandling';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import { hasTranslationKey, translateEntityProperty } from '@/utils/helper.ts';
import { I18nKey } from '@/utils/typings.ts';
import { QuantityMetaInfo } from '@/views/quantities/types.ts';
import { useTranslation } from 'i18next-vue';
import { cloneDeep } from 'lodash';
import { NIL, v7 as uuidv7 } from 'uuid';
import { useFieldValue } from 'vee-validate';
import { computed, inject, onMounted, ref, toRef, watchEffect } from 'vue';
import { toCreateDefinition } from '../../mappings';

type DefinitionParameterSetModalProps = {
  name: string;
  allowExtend: boolean;
};
const props = defineProps<DefinitionParameterSetModalProps>();

const quantityMetaInfo = inject<QuantityMetaInfo>('QuantityMetaInfo');

const definitionParameterSetIdentifier = defineModel<string | undefined>(
  'definitionParameterSetIdentifier',
  { default: undefined },
);
const definitionParameterSetFormMode = defineModel<FormMode>('definitionParameterSetFormMode', {
  default: 'add',
});

type DefinitionParameterSetModalEmits = {
  update: [DefinitionDto];
  close: [];
};
const emit = defineEmits<DefinitionParameterSetModalEmits>();
const originalDefinition = useFieldValue<DefinitionDto>(toRef(() => props.name));
const initialDefinition = ref<DefinitionDto>(cloneDeep(originalDefinition.value));
const { t } = useTranslation();
const confirmationOptions = getDefaultOptions(t);

const protocolOfDefinition = computed(() =>
  originalDefinition.value?.protocolId
    ? protocolsStore.getById(originalDefinition.value.protocolId)
    : undefined,
);

const unitsStore = useUnitsStore();
const protocolsStore = useProtocolsStore();

const newValue = (
  setIdentifier: string,
  parameterIdentifier: string,
  valueType?: CreateDefinitionDtoValueType | null,
  decimalPlaces?: number | null,
  unitAbbreviation?: string | null,
): DefinitionParameterValueDto => {
  return {
    id: NIL,
    setIdentifier,
    parameterIdentifier,
    value: Number.NaN,
    valueType,
    decimalPlaces,
    unitAbbreviation,
    $type:
      DefinitionParameterValueDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoNumericDtoDefinitionParameterValueDtoPtwPtw100AppZeroApplicationShared,
  };
};
const newSet = (setIdentifier: string): DefinitionParameterValueSetDto => {
  const existingValues = originalDefinition.value.definitionParameterValueSets?.find(
    (s) => s.setIdentifier === setIdentifier,
  )?.values;

  // Get unit abbreviation for the definition
  const unitAbbreviation = originalDefinition.value.unitId
    ? unitsStore.getById(originalDefinition.value.unitId)?.abbreviation
    : null;

  return {
    id: NIL,
    setIdentifier,
    values: [
      ...(existingValues ?? []),
      ...(originalDefinition.value.definitionParameters?.reduce<DefinitionParameterValueDto[]>(
        (acc, p) => {
          if (p.parameterIdentifier) {
            // Pass parameter's valueType, decimalPlaces, and unitAbbreviation to the value
            acc.push(
              newValue(
                setIdentifier,
                p.parameterIdentifier,
                p.valueType,
                p.decimalPlaces,
                unitAbbreviation,
              ),
            );
          }
          return acc;
        },
        [],
      ) ?? []),
    ],
    protocolId: null,
    $type:
      DefinitionParameterValueSetDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoNumericDtoDefinitionParameterValueSetDtoPtwPtw100AppZeroApplicationShared,
  };
};
const newDefinition = (): DefinitionDto => {
  const newSetIdentifier = uuidv7();
  return {
    ...initialDefinition.value,
    definitionParameterValueSets:
      typeof originalDefinition.value?.definitionParameterValueSets?.length === 'number'
        ? [...originalDefinition.value.definitionParameterValueSets, newSet(newSetIdentifier)]
        : [newSet(newSetIdentifier)],
  };
};

watchEffect(() => {
  if (
    definitionParameterSetFormMode.value === 'add' &&
    definitionParameterSetIdentifier.value === undefined
  ) {
    initialDefinition.value = newDefinition();
  } else if (
    definitionParameterSetFormMode.value === 'edit' &&
    typeof definitionParameterSetIdentifier.value === 'string'
  ) {
    initialDefinition.value = cloneDeep(originalDefinition.value);
  }
});

const { form } = useVeeForm<DefinitionDto>({
  schemaName: 'DefinitionDto',
  mode: definitionParameterSetFormMode.value,
  options: {
    initialValues: initialDefinition.value,
  },
});
const { handleSubmit, values: definitionFormValues, resetForm } = form;

const definitionParameterSetEditIndex = computed(() => {
  if (definitionParameterSetIdentifier.value === undefined) {
    if (typeof originalDefinition.value.definitionParameterValueSets?.length === 'number') {
      return originalDefinition.value.definitionParameterValueSets.length;
    } else {
      return 0;
    }
  } else {
    return originalDefinition.value.definitionParameterValueSets?.findIndex(
      (s) => s.setIdentifier === definitionParameterSetIdentifier.value,
    );
  }
});

const protocolOfParameterValueSet = ref<ProtocolDto | undefined>(undefined);
function updateProtocolOfParameterSet(updatedProtocol: ProtocolDto) {
  if (definitionParameterSetEditIndex.value !== undefined) {
    const parameterValueSet =
      definitionFormValues.definitionParameterValueSets?.[definitionParameterSetEditIndex.value];

    if (parameterValueSet) {
      // Create new array to trigger reactivity
      const updatedParameterValueSets = [
        ...(definitionFormValues.definitionParameterValueSets ?? []),
      ];

      // Create new object for the updated set
      updatedParameterValueSets[definitionParameterSetEditIndex.value] = {
        ...parameterValueSet,
        protocolId: updatedProtocol?.id,
      };

      // Update the form with new arrays to ensure Vue reactivity
      const updatedDefinition: DefinitionDto = {
        ...definitionFormValues,
        definitionParameterValueSets: updatedParameterValueSets,
      };

      resetForm({ values: updatedDefinition });
    }
    protocolOfParameterValueSet.value = updatedProtocol;
  }
}

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();

  if (definitionParameterSetFormMode.value === 'edit') {
    protocolOfParameterValueSet.value = protocolsStore.getById(
      definitionFormValues?.definitionParameterValueSets?.[definitionParameterSetEditIndex.value!]
        .protocolId,
    );
  }
});

const submit = handleSubmit(async (definitionDto) => {
  const createDefinitionDto = toCreateDefinition(definitionDto);

  const result = await api.numericQuantitiesApi.validateDefinitionWithValueSets(
    {
      createDefinitionDto,
    },
    [formBackendErrorHandler(form)],
  );

  if (result?.success) {
    emit('update', definitionDto);
  }
});

const { action: cancel } = useActionWithConfirmation({
  confirmedAction: () => {
    emit('close');
  },
  confirmationOptions,
});

const onCancel = (e?: Event) => {
  if (form.meta.value.dirty) return cancel({ param: e });
  emit('close');
};

defineExpose({
  submit,
  onCancel,
});

const currentSet = computed(() => {
  const dIndex = definitionParameterSetEditIndex.value ?? 0;
  return originalDefinition.value.definitionParameterValueSets?.[dIndex];
});

const isExistingSet = computed(() => !!currentSet.value?.id && currentSet.value.id !== NIL);

const disableValueField = computed(() => {
  if (!isExistingSet.value) return false;
  if (quantityMetaInfo?.isInAddMode) return false;
  if (quantityMetaInfo?.isInEditMode && definitionParameterSetFormMode.value === 'add')
    return false;
  return !!(
    quantityMetaInfo?.isInEditMode &&
    (quantityMetaInfo.isReferenced || quantityMetaInfo.isPredefined)
  );
});

const disableProtocolField = computed(() => currentSet.value?.origin === 'Predefined');
</script>

<template>
  <div>
    <v-row>
      <v-col>
        <TextField
          mode="details"
          :label="$t('Definition_one')"
          :modelValue="translateEntityProperty('name', originalDefinition)"
        />
      </v-col>
      <v-col>
        <TextField
          mode="details"
          :label="$t('DefinitionProtocol_one')"
          :modelValue="protocolOfDefinition?.name"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <Textarea
          mode="details"
          :label="$t('Description')"
          :modelValue="translateEntityProperty('description', originalDefinition) ?? undefined"
        />
      </v-col>
      <VDivider class="my-4" />
    </v-row>
    <form @submit="submit">
      <v-row
        v-for="(parameter, index) in definitionFormValues.definitionParameters"
        :key="parameter.id"
        data-testid="DefinitionParameterRow"
      >
        <v-col>
          <TextField
            mode="details"
            :label="$t('DefinitionParameterName')"
            :modelValue="
              hasTranslationKey(parameter?.name) ? $t(parameter?.name as I18nKey) : parameter?.name
            "
            data-testid="DefinitionParameterRow_Name"
          />
        </v-col>
        <v-col class="d-flex justify-end">
          <VeeNumberField
            class="w-50"
            :name="`definitionParameterValueSets[${definitionParameterSetEditIndex}].values[${index}].value`"
            :label="$t('Value')"
            :placeholder="$t('Required')"
            :suffix="
              parameter?.unitId ? unitsStore.getById(parameter.unitId)?.abbreviation : undefined
            "
            :format="{ valueType: parameter?.valueType }"
            :validateOnValueUpdate="true"
            data-testid="DefinitionParameterRow_ValueField"
            :disabled="disableValueField"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <Select
            :clearable="true"
            :label="$t('DefinitionParameterProtocol_one')"
            :items="protocolsStore.options"
            :modelValue="protocolOfParameterValueSet"
            data-testid="DefinitionParameter_Protocol"
            :disabled="disableProtocolField"
            @update:modelValue="updateProtocolOfParameterSet"
          />
        </v-col>
      </v-row>
    </form>
  </div>
</template>
